import { CSSResultGroup, html } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { ButtonElement } from 'elements/_shared/formElement.js'
import { buttonGroupStyle, style } from './button.style.js'
import { classMap } from 'lit/directives/class-map.js'
import { LinkTargetTypes } from 'elements/link/link.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'


export const buttonVariants = ['default', 'primary', 'secondary', 'error', 'info', 'success', 'warning'] as const
export type buttonVariantTypes = typeof buttonVariants

export const buttonSizes = ['default', 'small'] as const
export type buttonSizeTypes = typeof buttonSizes

export const fitVariants = ['auto', 'fill', 'square'] as const
export type fitVariantsTypes = typeof fitVariants

@customElement('cse-button')
export class CSEButton extends ButtonElement {
  static override styles: CSSResultGroup = [hostStyle, style, buttonGroupStyle]

  @property({ type: String })
  variant: buttonVariantTypes[number] = 'default'

  @property({ type: String })
  size: buttonSizeTypes[number] = 'default'

  @property({ type: String })
  fit: fitVariantsTypes[number] = 'auto'

  @property({ type: Boolean })
  disabled = false

  @property({ type: Boolean })
  loading = false

  @property({ type: String })
  url = ''

  @property({ type: String })
  target: LinkTargetTypes[number] = '_self'

  @property({ type: String, attribute: 'aria-label' })
  override ariaLabel = ''

  @property({ type: String })
  rel = ''

  private renderButtonContents = () => html`
    <slot name="prefix" class="prefix"></slot>
    <slot></slot>
    <slot name="suffix" class="suffix"></slot>
  `

  override render() {
    const isLink = this.url !== ''
    const isDisabled = this.disabled || this.loading
    const classes = classMap({
      'cse-button': true,
      [`variant-${this.variant}`]: true,
      [`size-${this.size}`]: true,
      'full-width': this.fit==='fill',
      'square': this.fit==='square',
      'loading': this.loading
    })
    const content = this.loading ? html`<cse-loader></cse-loader>` : this.renderButtonContents()

    if (isLink) {
      return html`
        <a
          href=${this.url}
          target=${this.target}
          class="${classes}"
          aria-label="${this.ariaLabel}"
          ?disabled=${isDisabled}
          rel=${this.rel}
        >
          ${content}
        </a>
      `
    } else {
      return html`
        <button
          type=${this.buttonType}
          class="${classes}"
          ?disabled=${isDisabled}
          aria-label="${this.ariaLabel}"
          @click=${this.dispatchSubmit}
        >
          ${content}
        </button>
      `
    }
  }
}


declare global {
  interface HTMLElementTagNameMap {
    'cse-button': CSEButton
  }
}
